// Select2 component for searchable dropdowns
import React from 'react';
import Select from 'react-select';
import './Select2.css';

const Select2 = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  isLoading = false, 
  isDisabled = false,
  isSearchable = true,
  className = '',
  name = '',
  isClearable = true,
  menuHeight = 300,
  menuPlacement = 'bottom',
  filterOption,
  ...props 
}) => {
  
  // Custom styles for the Select component
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '8px',
      borderColor: state.isFocused ? '#007bff' : '#ddd',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0,123,255,.25)' : 'none',
      '&:hover': {
        borderColor: '#007bff',
      },
      fontSize: '16px',
      backgroundColor: isDisabled ? '#f8f9fa' : 'white'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
      fontSize: '16px'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#495057',
      fontSize: '16px'
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#007bff' 
        : state.isFocused 
        ? '#e9ecef' 
        : 'white',
      color: state.isSelected ? 'white' : '#495057',
      fontSize: '16px',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef',
      }
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      color: '#007bff'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#6c757d',
      '&:hover': {
        color: '#495057'
      }
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6c757d',
      '&:hover': {
        color: '#495057'
      }
    })
  };
  
  // Format options to ensure consistent structure
  const formattedOptions = options?.map(option => ({
    ...option, // Preserve all properties first
    value: option.value || option.id,
    label: option.label || option.name
  })) || [];
  
  // Find the selected option object - Fix the value matching
  const selectedOption = value 
    ? formattedOptions.find(option => 
        String(option.value) === String(value) || 
        (value && typeof value === 'object' && String(option.value) === String(value.value))
      )
    : null;
  
  const handleChange = (selectedOption) => {
    if (onChange) {
      // Pasar solo el valor para compatibilidad con los handlers existentes
      onChange(selectedOption ? selectedOption.value : null);
    }
  };
  
  // Custom filter function for large datasets
  const customFilterOption = (option, inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      // For large datasets, require minimum 2 characters
      return formattedOptions.length <= 100;
    }
    const searchValue = inputValue.toLowerCase();
    const optionLabel = option.label.toLowerCase();
    return optionLabel.includes(searchValue);
  };

  return (
    <Select
      name={name}
      className={`react-select ${className}`}
      classNamePrefix="react-select"
      options={formattedOptions}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder || 'Seleccionar...'}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      isClearable={isClearable}
      styles={customStyles}
      maxMenuHeight={menuHeight}
      menuPlacement={menuPlacement}
      filterOption={filterOption || (formattedOptions.length > 100 ? customFilterOption : undefined)}
      blurInputOnSelect={true}
      closeMenuOnSelect={true}
      noOptionsMessage={({ inputValue }) => {
        // Use custom noOptionsMessage if provided via props
        if (props.noOptionsMessage && typeof props.noOptionsMessage === 'function') {
          return props.noOptionsMessage({ inputValue });
        }
        
        // Default behavior
        return inputValue && inputValue.length >= 2 ? 
          `No se encontraron opciones con "${inputValue}"` : 
          inputValue ? 'Escribe al menos 2 caracteres para buscar' :
          formattedOptions.length > 100 ? 'Escribe para buscar' :
          'No hay opciones disponibles';
      }}
      {...props}
    />
  );
};

export default Select2;
